import React, { useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Alert } from 'antd';
import { usePost } from 'hooks';
import SsoLayout from './SsoLayout';
import {
  headingStyle,
  textStyle,
  spinnerContainerStyle,
  buttonStyle,
  spinnerSvg,
  errorContainerStyle,
} from './styles';

const SsoSignIn = () => {
  const location = useLocation();
  const { push } = useHistory();
  const [error, setError] = useState(null);

  // Hook for calling the SSO hash lookup endpoint
  const [ssoLookupState, sendSsoLookup] = usePost({
    url: '/api/auth/sso-issuer',
    body: null,
    onMount: false,
  });

  useEffect(() => {
    const initiateSignIn = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const authUrl = params.get('authUrl'); // SP-initiated flow
        const hash = params.get('hash'); // Hash-based flow

        if (authUrl) {
          // SP-initiated flow - we already have the complete authorization URL
          window.location.href = decodeURIComponent(authUrl);
          return;
        }

        if (hash) {
          // Hash-based flow - look up SSO configuration by hash
          sendSsoLookup({ hash });
        } else {
          // No valid parameters provided
          setError('No SSO parameters provided. Please use a valid SSO link.');
        }
      } catch (error) {
        console.error('Error in SSO sign-in flow:', error);
        setError('An error occurred during SSO setup. Please try again.');
      }
    };

    initiateSignIn();
  }, [location, sendSsoLookup]);

  // Handle the response from the SSO hash lookup
  useEffect(() => {
    if (ssoLookupState.loading) {
      return;
    }

    if (ssoLookupState.error) {
      console.error('SSO lookup error:', ssoLookupState.error);
      setError(
        'Unable to find SSO configuration. Please contact your administrator.'
      );
      return;
    }

    if (ssoLookupState.data || ssoLookupState.res) {
      const responseData = ssoLookupState.data || ssoLookupState.res;
      const { hasSsoEnabled, authorizationUrl } = responseData;

      if (hasSsoEnabled && authorizationUrl) {
        // Redirect to the SSO provider
        window.location.href = authorizationUrl;
      } else {
        setError(
          'SSO is not enabled for your organization. Please contact your administrator.'
        );
      }
    }
  }, [ssoLookupState]);

  // Show error with option to go to login page
  if (error) {
    return (
      <SsoLayout>
        <div style={headingStyle}>SSO Login Error</div>
        <div style={errorContainerStyle}>
          <Alert
            message='SSO Configuration Error'
            description={error}
            type='error'
            showIcon
          />
        </div>
        <button onClick={() => push('/login')} style={buttonStyle}>
          Go to Login Page
        </button>
      </SsoLayout>
    );
  }

  return (
    <SsoLayout>
      <div style={headingStyle}>
        {ssoLookupState.loading
          ? 'Setting up SSO login…'
          : 'Redirecting to SSO provider…'}
      </div>
      <div style={textStyle}>
        {ssoLookupState.loading
          ? 'Please wait while we prepare your secure login.'
          : "Please wait while we connect you to your organization's secure login."}
      </div>
      <div style={spinnerContainerStyle}>{spinnerSvg}</div>
    </SsoLayout>
  );
};

export default SsoSignIn;
